import { Icon, tailwindPreset } from '@southfields-digital/mpxlive-components';

import Select, { Option } from 'src/components/Select';

import styles from './CanvasForm.module.scss';
import { formatConflictMessage, getOutputConflicts } from './utils';

type CanvasFormProps = {
  state: (RundownCanvas | RundownTemplateCanvas)[];
  canvases: Canvas[];
  outputs?: Output[];
  canvasBackgrounds?: CanvasBackground[];
  stateUpdated?: (state: RundownCanvas[]) => void;
  disabled?: boolean;
};

const emptyOption: Option = { label: ' - ', value: '' };

const CanvasForm = ({
  state,
  canvases,
  outputs,
  canvasBackgrounds,
  stateUpdated,
  disabled = false,
}: CanvasFormProps) => {
  const backgroundOptions: Option[] = [
    emptyOption,
    ...(canvasBackgrounds?.map(({ name, id }) => ({ label: name, value: id })) || []),
  ];
  const outputOptions: Option[] = [
    emptyOption,
    ...(outputs?.map(({ id, name, disabled }) => ({
      label: name,
      value: id,
      disabled,
    })) || []),
  ];

  const handleSelectOutput = (canvasId: string, outputId: string) => {
    const output = outputs?.find(({ id }) => outputId === id);
    const updatedState = state.map((canvas) =>
      canvas.id !== canvasId ? canvas : { ...canvas, output: output }
    );

    if (stateUpdated) {
      stateUpdated(updatedState);
    }
  };

  const handleSelectBackground = (
    canvas: RundownCanvas | RundownTemplateCanvas,
    backgroundId: CanvasBackground['id']
  ) => {
    const updatedState = state.map((c) =>
      c.id !== canvas.id ? c : { ...c, canvasBackgroundId: backgroundId ? backgroundId : null }
    );

    if (stateUpdated) {
      stateUpdated(updatedState);
    }
  };

  const conflicts = getOutputConflicts(state, canvases);

  return (
    <table className={styles.CanvasGraphicsForm}>
      <thead>
        <tr>
          <th className={styles.CanvasColumn}>Canvas</th>
          {outputs ? <th>Output</th> : null}
          <th>Background</th>
        </tr>
      </thead>
      <tbody>
        {state?.map((rundownCanvas) => {
          const inputDisabled = disabled || !rundownCanvas.graphics.length;
          const canvas = canvases.find(({ id }) => id === rundownCanvas.id);
          const conflict = conflicts.find((c) => c.output.id === rundownCanvas.output?.id);
          return (
            <tr key={rundownCanvas.id}>
              <td className={styles.CanvasColumn}>
                <div className={styles.CanvasColumnContent}>
                  <div>
                    {rundownCanvas.name}
                    {canvas && (
                      <div className={styles.Resolution}>
                        {canvas.width}×{canvas.height}
                      </div>
                    )}
                  </div>
                  {conflict && (
                    <div title={formatConflictMessage(conflict)}>
                      <Icon
                        icon="warning"
                        color={tailwindPreset.theme.extend.colors.warning}
                        weight="duotone"
                      />
                    </div>
                  )}
                </div>
              </td>
              {outputs ? (
                <td>
                  <Select
                    disabled={inputDisabled}
                    onChange={(e) => handleSelectOutput(rundownCanvas.id, e.target.value)}
                    options={outputOptions}
                    value={rundownCanvas.output?.id || emptyOption.value}
                  />
                </td>
              ) : null}
              <td>
                <Select
                  disabled={inputDisabled}
                  onChange={(e) => handleSelectBackground(rundownCanvas, e.target.value)}
                  options={backgroundOptions}
                  value={rundownCanvas.canvasBackgroundId || emptyOption.value}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default CanvasForm;
